<script setup lang="ts">
import AlertBox from './AlertBox.vue';
import LaddaButton from './LaddaButton.vue';
import { BModal, BButton, BFormInput } from 'bootstrap-vue-next';
import type { ConfirmDialog } from '../plugins/confirm';
import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';
import type { ButtonVariant } from 'node_modules/bootstrap-vue-next/dist/src/types';

// eslint-disable-next-line no-unused-vars
type ResolveFunction = (instance: ConfirmDialog) => PromiseLike<ConfirmDialog>;
const props = withDefaults(
  defineProps<{
    resolve: ResolveFunction;
    title?: string;
    text?: string;
    html?: boolean;
    autoClose?: boolean;
    yesText?: string;
    noText?: string;
    yesVariant?: ButtonVariant;
    noVariant?: ButtonVariant;
    error?: string;
    hard?: string;
  }>(),
  {
    yesVariant: 'primary',
    noVariant: 'secondary',
    hard: '',
    html: false,
    title: '',
    text: '',
    yesText: '',
    noText: '',
    error: ''
  }
);

const show = ref(false);
const loading = ref(false);
const error = ref(props.error || '');
const hardInput = ref('');
const instance = reactive({
  result: false,
  close: (ok: boolean) => {
    show.value = false;
    return resolveCB(ok);
  }
});

onMounted(() => {
  show.value = true;
});

function resolveCB(value: boolean) {
  instance.result = value;
  setTimeout(() => {
    props.resolve(instance);
  }, 300);
}

function onYes() {
  error.value = '';
  if (props.autoClose) {
    instance.close(true);
    return;
  }
  loading.value = true;
  instance.result = true;
  props.resolve(instance);
}

function onNo() {
  error.value = '';
  instance.close(false);
}

const isDisabled = computed(() => {
  return props.hard ? props.hard != hardInput.value : false;
});

const current = getCurrentInstance();
function unmount() {
  setTimeout(() => {
    current?.root.appContext.app.unmount();
  }, 300);
}
</script>

<template lang="pug">
BModal(tabindex="-1" role="dialog", :title="title || 'Are you sure?'", :model-value="show", :hide-header-close="true" :no-close-on-esc="true" :no-close-on-backdrop="true" @hidden="unmount")
  AlertBox(v-if="error" variant="danger") {{ error }}
  // eslint-disable-next-line vue/no-v-html
  p(v-if="html", v-html="text")
  p(v-else) {{ text }}
  template(v-if="hard.length > 0")
    label Type #[strong "{{ hard }}"] to confirm.
    BFormInput(v-model="hardInput" :placeholder="hard")
  template(#footer)
    BButton(:variant="noVariant" @click="onNo") {{ noText || 'No' }}
    LaddaButton.btn(:variant="yesVariant" :is-loading="loading" :disabled="isDisabled" @click="onYes") {{ yesText || 'Yes' }}
</template>
